import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, ShoppingBag, Share2, Gift } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";
import api from "@/lib/api";
import WishlistGrid from "@/components/WishlistGrid";
import { toast } from "sonner";

export default function Wishlist() {
  useSeo("Wishlist", "Your saved Chewie & Solo favourites.");
  const { wishlist, moveManyToCart } = useStore();
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [giftHint, setGiftHint] = useState({ has_gift: false, count: 0 });

  useEffect(() => {
    api.get("/products?limit=100").then(({ data }) => setProducts(data.products)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!user) { setGiftHint({ has_gift: false, count: 0 }); return; }
    api.get("/wishlist/gift-hint").then(({ data }) => setGiftHint(data)).catch(() => {});
  }, [user]);

  const byId = Object.fromEntries(products.map((p) => [String(p.id), p]));
  const entries = wishlist.map((w) => ({ ...w, product: byId[w.id] })).filter((e) => e.product);

  const handleMoveAll = () => {
    const n = moveManyToCart(entries);
    if (n > 0) toast.success(`${n} item${n > 1 ? "s" : ""} moved to your bag 🐾`);
    else toast.info("Pick a size/colour on your saved items first.");
  };

  const handleShare = async () => {
    try {
      const { data } = await api.post("/wishlist/share");
      const url = `${window.location.origin}/wishlist/shared/${data.token}`;
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied — send it to family for gifting! 💛");
    } catch {
      toast.error("Couldn't create a share link. Please try again.");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14 min-h-[60vh]">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[#19324A] mb-2">Your Wishlist 🐾</h1>
          <p className="text-[#19324A]/60">The goodies you're keeping an eye on.</p>
        </div>
        {entries.length > 0 && (
          <div className="flex gap-3">
            <button onClick={handleMoveAll} data-testid="wishlist-move-all" className="btn-primary !py-2.5">
              <ShoppingBag className="w-4 h-4" /> Move all to bag
            </button>
            {user && (
              <button onClick={handleShare} data-testid="wishlist-share" className="btn-outline !py-2.5">
                <Share2 className="w-4 h-4" /> Share
              </button>
            )}
          </div>
        )}
      </div>
      {giftHint.has_gift && (
        <div data-testid="wishlist-gift-hint" className="mb-6 rounded-3xl bg-[#FFD93D]/25 border-2 border-[#FFD93D] px-5 py-4 flex items-start gap-3">
          <span className="w-10 h-10 rounded-full bg-white flex items-center justify-center shrink-0 soft-shadow"><Gift className="w-5 h-5 text-[#FF6B9D]" /></span>
          <div>
            <p className="font-display font-bold text-[#19324A]">Psst… someone's been peeking at your list 🎁</p>
            <p className="text-sm text-[#19324A]/70">{giftHint.count === 1 ? "A surprise has been reserved for you" : `${giftHint.count} surprises have been reserved for you`} — we won't spoil which. Act surprised!</p>
          </div>
        </div>
      )}
      {entries.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-3xl soft-shadow">
          <Heart className="w-16 h-16 text-[#FF6B9D]/40 mx-auto" />
          <p className="font-display font-bold text-xl text-[#19324A] mt-4">No favourites yet.</p>
          <p className="text-[#19324A]/60 mt-1">Tap the heart on any product to save it here.</p>
          <Link to="/shop" className="btn-primary mt-6 inline-flex">Browse the Shop</Link>
        </div>
      ) : (
        <WishlistGrid entries={entries} />
      )}
    </div>
  );
}
